import Decimal from 'decimal.js';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { TankContent } from 'src/types/ViewModel/Stowage/Manifest/TankContent';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { sequenceValue, sortOperationsBySequence } from '../cargoSequenceUtils';
import { isVoyageClosedOrCompleted } from '../stowageRotationUtils';

export interface SequenceSnapshot {
    berth: RotationBerthCall;
    operations: RotationCargoOperation[];
    arrival: Map<number, TankContent>;
    during: Map<number, TankContent>;
    departure: Map<number, TankContent>;
    invalidDischarges: { operation: RotationCargoOperation; tank: Tank }[];
}

export const buildCargoSequenceSnapshots = (plan: StowagePlan, tanks: Tank[], firstVoyage = 202401): SequenceSnapshot[] => {
    const inventory = new Map<number, Map<string, CargoContent>>();
    const result: SequenceSnapshot[] = [];
    const itemsByOperation = new Map<string, typeof plan.stowageItems>();
    const isEligibleVoyage = (port: StowagePlan['rotation'][number]) =>
        Number(port.voyageNumber) >= firstVoyage && !isVoyageClosedOrCompleted(port.voyageStatus);
    const forgottenVoyages = new Set((plan.rotation ?? []).filter((port) => !isEligibleVoyage(port)).map((port) => port.voyageNumber));
    const cargoesUnloadedAfterCutoff = new Set<string>(
        (plan.rotation ?? [])
            .filter(isEligibleVoyage)
            .flatMap((port) => port.berthCalls.flatMap((berth) => berth.cargoOperations ?? []))
            .filter((operation) => operation.operationType === 'Unloading')
            .map((operation) => operation.cargoGuid)
    );
    for (const item of plan.stowageItems ?? []) {
        const items = itemsByOperation.get(item.cargoOperationGuid) ?? [];
        items.push(item);
        itemsByOperation.set(item.cargoOperationGuid, items);
    }
    for (const port of plan.rotation ?? []) {
        const eligible = isEligibleVoyage(port);
        if (eligible)
            inventory.forEach((contents) =>
                contents.forEach((content, guid) => {
                    const forgotten = Number(content.cargo.voyageNumber) < firstVoyage || forgottenVoyages.has(content.cargo.voyageNumber);
                    if (forgotten && !cargoesUnloadedAfterCutoff.has(guid)) contents.delete(guid);
                })
            );
        for (const berth of port.berthCalls.filter(
            (call) =>
                call.berthId > 0 ||
                (call.berthId === 0 &&
                    (call.cargoOperations ?? []).some(
                        (operation) => operation.operationType !== 'Transit' && itemsByOperation.has(operation.cargoOperationGuid)
                    ))
        )) {
            const snapshot = (): Map<number, TankContent> =>
                new Map(
                    tanks.map((tank) => [
                        tank.tankId,
                        {
                            tank,
                            berthCall: berth,
                            portCall: port,
                            isSequenceInventory: true,
                            cargoContents: Array.from(inventory.get(tank.tankId)?.values() ?? [])
                                .filter((content) => content.weight.gt(0))
                                .map((content) => ({ ...content })),
                        },
                    ])
                );
            const apply = (operations: RotationCargoOperation[], positiveOnly: boolean) => {
                const movements = new Map<string, { operation: RotationCargoOperation; item: (typeof plan.stowageItems)[number]; weight: Decimal }>();
                const invalidDischarges: SequenceSnapshot['invalidDischarges'] = [];
                for (const operation of operations) {
                    for (const item of itemsByOperation.get(operation.cargoOperationGuid) ?? []) {
                        if (positiveOnly && item.weight.lte(0)) continue;
                        const key = `${item.tankId}-${operation.cargoGuid}`;
                        const previous = movements.get(key);
                        movements.set(key, { operation, item, weight: (previous?.weight ?? new Decimal(0)).plus(item.weight) });
                    }
                }
                movements.forEach(({ operation, item, weight: movement }) => {
                    const contents = inventory.get(item.tankId) ?? new Map<string, CargoContent>();
                    const existing = contents.get(operation.cargoGuid);
                    const weight = (existing?.weight ?? new Decimal(0)).plus(movement);
                    if (!positiveOnly && weight.lt(0)) invalidDischarges.push({ operation, tank: item.tank });
                    contents.set(operation.cargoGuid, {
                        cargo: operation.cargo,
                        cargoOperation: operation,
                        stowage: item,
                        weight: Decimal.max(0, weight),
                        weightInTransit: new Decimal(0),
                        billOfLadingWeight: null,
                        shipFigure: item.shipFigure,
                        isShipFigureAdjusted: item.isShipFigureAdjusted,
                    });
                    inventory.set(item.tankId, contents);
                });
                return invalidDischarges;
            };
            const ordered = sortOperationsBySequence((berth.cargoOperations ?? []).filter((operation) => operation.operationType !== 'Transit'));
            const groups: RotationCargoOperation[][] = [];
            if (!ordered.length) groups.push([]);
            if (ordered.some((operation) => !Number.isFinite(sequenceValue(operation)))) {
                groups.push(ordered);
            } else {
                for (const operation of ordered) {
                    const last = groups[groups.length - 1];
                    if (last && sequenceValue(last[0]) === sequenceValue(operation)) last.push(operation);
                    else groups.push([operation]);
                }
            }
            for (const operations of groups) {
                const arrival = snapshot();
                const before = new Map(Array.from(inventory.entries()).map(([id, contents]) => [id, new Map(contents)]));
                apply(operations, true);
                const during = snapshot();
                inventory.clear();
                for (const [id, contents] of Array.from(before.entries())) inventory.set(id, contents);
                const invalidDischarges = apply(operations, false);
                if (eligible) result.push({ berth, operations, arrival, during, departure: snapshot(), invalidDischarges });
            }
        }
    }
    return result;
};

export const getTankContentsForCargoOperation = (
    plan: StowagePlan,
    operation: RotationCargoOperation,
    tanks: Tank[]
): Map<number, TankContent> | undefined => {
    const snapshots = plan.manifest?.cargoSequenceSnapshots ?? buildCargoSequenceSnapshots(plan, tanks);
    const snapshot = snapshots.find(
        (item) =>
            item.berth.berthCallGuid === operation.berthCallGuid &&
            item.operations.some((candidate) => candidate.cargoOperationGuid === operation.cargoOperationGuid)
    );
    const contents = operation.operationType === 'Unloading' ? snapshot?.arrival : snapshot?.during;
    return contents;
};
